import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { ASSETS } from "../../assets/assets";
import { SectionHeader } from "./Features";

export default function Gallery() {
  const { t } = useLang();
  const [tab, setTab] = useState("desktop");
  const [idx, setIdx] = useState(0);

  const desktopShots = [
    { src: ASSETS.desktop.dashboardDark, caption: t.gallery.captions.d1 },
    { src: ASSETS.desktop.mapaMesas, caption: t.gallery.captions.d2 },
    { src: ASSETS.desktop.categorias, caption: t.gallery.captions.d3 },
    { src: ASSETS.desktop.procesarPago, caption: t.gallery.captions.d4 },
    { src: ASSETS.desktop.dashboardLight, caption: t.gallery.captions.d5 },
  ];
  const mobileShots = [
    { src: ASSETS.mobile.login, caption: t.gallery.captions.m1 },
    { src: ASSETS.mobile.mapaMesas, caption: t.gallery.captions.m2 },
    { src: ASSETS.mobile.categorias, caption: t.gallery.captions.m3 },
    { src: ASSETS.mobile.procesarPago, caption: t.gallery.captions.m4 },
    { src: ASSETS.mobile.dashboard, caption: t.gallery.captions.m5 },
  ];

  const shots = tab === "desktop" ? desktopShots : mobileShots;
  const safeIdx = idx % shots.length;
  const current = shots[safeIdx];

  const switchTab = (next) => {
    setTab(next);
    setIdx(0);
  };
  const prev = () => setIdx((i) => (i - 1 + shots.length) % shots.length);
  const next = () => setIdx((i) => (i + 1) % shots.length);

  return (
    <section id="gallery" className="relative py-20 md:py-32 overflow-hidden">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] glow-teal opacity-60" />
      </div>
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeader
          eyebrow={t.gallery.eyebrow}
          title={t.gallery.title}
          subtitle={t.gallery.subtitle}
        />

        {/* Tabs */}
        <div className="mt-10 flex justify-center">
          <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-full p-1" data-testid="gallery-tabs">
            <button
              onClick={() => switchTab("desktop")}
              data-testid="gallery-tab-desktop"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                tab === "desktop" ? "bg-brand-orange text-white" : "text-white/70 hover:text-white"
              }`}
            >
              {t.gallery.tabs.desktop}
            </button>
            <button
              onClick={() => switchTab("mobile")}
              data-testid="gallery-tab-mobile"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                tab === "mobile" ? "bg-brand-orange text-white" : "text-white/70 hover:text-white"
              }`}
            >
              {t.gallery.tabs.mobile}
            </button>
          </div>
        </div>

        {/* Slide */}
        <div className="mt-10 md:mt-14 relative">
          <div className="absolute -inset-8 -z-10 glow-orange opacity-80 blur-2xl" />

          <AnimatePresence mode="wait">
            <motion.div
              key={`${tab}-${safeIdx}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="mx-auto"
              data-testid={`gallery-slide-${tab}-${safeIdx}`}
            >
              {tab === "desktop" ? (
                <div className="laptop-frame mx-auto max-w-3xl">
                  <div className="laptop-frame__screen">
                    <img src={current.src} alt={current.caption} className="w-full h-full object-cover object-top" loading="lazy" />
                  </div>
                  <div className="laptop-frame__base" />
                </div>
              ) : (
                <div className="phone-frame mx-auto w-[240px] sm:w-[280px]">
                  <div className="phone-frame__notch" />
                  <div className="phone-frame__screen">
                    <img src={current.src} alt={current.caption} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Caption + controls */}
          <div className="mt-7 flex items-center justify-center gap-5">
            <button
              onClick={prev}
              aria-label="previous"
              data-testid="gallery-prev"
              className="w-11 h-11 rounded-full bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="text-center min-w-[160px]">
              <p className="text-sm md:text-base text-white font-medium" data-testid="gallery-caption">
                {current.caption}
              </p>
              <p className="text-xs text-white/40 mt-1">
                {safeIdx + 1} / {shots.length}
              </p>
            </div>
            <button
              onClick={next}
              aria-label="next"
              data-testid="gallery-next"
              className="w-11 h-11 rounded-full bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:text-white flex items-center justify-center transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Dots */}
          <div className="mt-4 flex items-center justify-center gap-2">
            {shots.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`go to slide ${i + 1}`}
                data-testid={`gallery-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === safeIdx ? "w-6 bg-brand-orange" : "w-1.5 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
